import type {
    BuildingCategory,
    BuildingInStoreInfo,
    ContentsPropertyManagement,
    LocalizedString,
    ManagedPoiContent,
    PoiContent,
} from '@/services/dto';

const convertCategory = (data: { [key: string]: string }[]) => {
    return data.reduce<Record<string, string>>((acc, current) => {
        if (!current) return acc;
        
        Object.keys(current).forEach((key) => {
            if (acc[key]) {
                acc[key] += `, ${current[key]}`;
            } else {
                acc[key] = current[key];
            }
        });
        return acc;
    }, {});
};

const resolveContent = (fieldKey: string, poi: PoiContent, buildingCategoriesSelected?: BuildingCategory[]) => {
    if (fieldKey === 'name') {
        return poi.name;
    }

    if (fieldKey === 'clientId') {
        const studioId = poi.studioContentsId || '';
        return { ko: studioId, en: studioId, jp: studioId, 'zh-TW': studioId, 'zh-CN': studioId };
    }

    if (fieldKey === 'category') {
        const categoryData = poi.categories?.map((cat) => {
            return buildingCategoriesSelected?.find((bCat) => bCat.categoryCode === cat)?.name;
        });
        return convertCategory(categoryData ?? []);
    }

    const raw = (poi.contents as Record<string, unknown> | undefined)?.[fieldKey];
    const isValidObject = raw !== null && typeof raw === 'object' && !Array.isArray(raw);

    return isValidObject
        ? (raw as LocalizedString)
        : { ko: String(raw ?? ''), en: String(raw ?? ''), jp: String(raw ?? ''), 'zh-TW': String(raw ?? ''), 'zh-CN': String(raw ?? '') };
};

const buildManagedContents = (
    poi: PoiContent,
    propertyManagement: ContentsPropertyManagement[],
    buildingCategoriesSelected?: BuildingCategory[]
): ManagedPoiContent[] =>
    propertyManagement
        .filter((item) => item.visible !== false)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((item) => ({
            field_key: item.field_key,
            name: item.content,
            attribute_type: item.attribute_type,
            display_type: item.display_type,
            content: resolveContent(item.field_key, poi, buildingCategoriesSelected),
        }));

export const enrichPoiWithManagedContents = (
    poi: PoiContent,
    propertyManagement: ContentsPropertyManagement[],
    buildingCategoriesSelected?: BuildingCategory[]
): PoiContent => ({
    ...poi,
    managedContents: buildManagedContents(poi, propertyManagement, buildingCategoriesSelected),
});

const getContentsPropertyManagement = (
    building: BuildingInStoreInfo,
    storeLevelPropertyManagement?: ContentsPropertyManagement[]
): ContentsPropertyManagement[] => building.contentsPropertyManagement ?? storeLevelPropertyManagement ?? [];

export const enrichPoiForBuilding = (
    poi: PoiContent,
    building: BuildingInStoreInfo,
    storeLevelPropertyManagement?: ContentsPropertyManagement[]
): PoiContent => {
    if (poi.managedContents?.length) {
        return poi;
    }

    const propertyManagement = getContentsPropertyManagement(building, storeLevelPropertyManagement);
    if (!propertyManagement.length) {
        return poi;
    }

    return enrichPoiWithManagedContents(
        poi,
        propertyManagement,
        building.buildingCategoriesSelected ?? building.buildingCategories
    );
};

export const findPoiContentInBuildings = (
    studioPoiId: string,
    buildings: BuildingInStoreInfo[]
): { poi: PoiContent; building: BuildingInStoreInfo } | null => {
    let fallback: { poi: PoiContent; building: BuildingInStoreInfo } | null = null;

    for (const building of buildings) {
        const candidateLists = [
            building.filterPoiContents?.shop,
            building.filterPoiContents?.fnb,
            building.filterPoiContents?.facility,
            building.filterPoiContents?.otherUtility,
            building.poiContents,
        ];

        for (const list of candidateLists) {
            if (!Array.isArray(list)) {
                continue;
            }

            for (const poi of list) {
                if (poi.studioPoiId !== studioPoiId) {
                    continue;
                }

                if (!fallback) {
                    fallback = { poi, building };
                }

                if (poi.managedContents?.length) {
                    return { poi, building };
                }
            }
        }
    }

    return fallback;
};
